/**
 * Utility functions for content management
 */

/**
 * Generates a unique content key based on page and field
 * @param page The page identifier
 * @param field The field identifier
 * @returns A dot-notation content key
 */
export function generateContentKey(page: string, field: string): string {
    return `${page}.${field}`;
}

/**
 * Parses a content key into its page and field components
 * @param key The content key in dot notation
 * @returns An object with page and field properties
 */
export function parseContentKey(key: string): { page: string; field: string } {
    const [page, ...fieldParts] = key.split('.');
    const field = fieldParts.join('.');
    return { page, field };
}

/**
 * Formats nested content keys for display
 * @param key The content key
 * @returns A formatted display string
 */
export function formatContentKey(key: string): string {
    return key.split('.').map(part =>
        part.charAt(0).toUpperCase() + part.slice(1)
    ).join(' › ');
}

/**
 * Content utility functions for working with nested content structures
 */

/**
 * Updates a nested property in an object using a dot-notation path
 * without modifying the original object (immutable)
 * 
 * @param obj The original object to update
 * @param path Dot-notation path to the property (e.g., 'user.profile.name')
 * @param value The new value to set
 * @returns A new object with the updated property
 */
export function updateNestedContent(obj: any, path: string, value: any): any {
  const result = { ...obj };
  const pathParts = path.split('.');
  let current = result;
  
  // Navigate to the parent object of the field to update
  for (let i = 0; i < pathParts.length - 1; i++) {
    const part = pathParts[i];
    
    // If current part is a number, treat it as array index
    if (!isNaN(Number(part)) && Array.isArray(current)) {
      const index = Number(part);
      current[index] = current[index] || (i === pathParts.length - 2 ? {} : []);
      current = current[index];
    } else {
      current[part] = current[part] || (i === pathParts.length - 2 ? {} : []);
      current = current[part];
    }
  }

  // Set the value at the final property
  const finalProp = pathParts[pathParts.length - 1];
  current[finalProp] = value;
  
  return result;
}

/**
 * Gets a nested property from an object using a dot-notation path
 * 
 * @param obj The object to get the property from
 * @param path Dot-notation path to the property
 * @param defaultValue Default value to return if the property doesn't exist
 * @returns The property value or the default value
 */
export function getNestedValue(obj: any, path: string, defaultValue: any = undefined): any {
  if (!obj) return defaultValue;
  
  const pathParts = path.split('.');
  let current = obj;
  
  for (const part of pathParts) {
    // Handle array indices
    if (!isNaN(Number(part)) && Array.isArray(current)) {
      const index = Number(part);
      current = current[index];
    } else {
      current = current[part];
    }
    
    // Return default if we hit undefined/null before the end of the path
    if (current === undefined || current === null) {
      return defaultValue;
    }
  }
  
  return current;
}

/**
 * Merges edited content into the existing content structure
 * 
 * @param existingContent The original content object
 * @param edits Object with dot-notation keys and their values
 * @returns A new object with all edits applied
 */
export function mergeContentEdits(existingContent: any, edits: Record<string, any>): any {
  let updatedContent = { ...existingContent };
  
  // Apply each edit to the content structure
  Object.entries(edits).forEach(([key, value]) => {
    updatedContent = updateNestedContent(updatedContent, key, value);
  });
  
  return updatedContent;
} 